import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import { Environment } from '@cobliteam/react-environments';
import type { FeedItem } from '@knocklabs/client';
import { useKnockClient } from '@knocklabs/react';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { DRAWER_QUERY_PARAM, parseDrawerParam } from '../core/deepLink';
import type { NotificationType } from '../core/NotificationType';
import useNotificationDrawerStore from '../core/store/useNotificationDrawerStore';
import { getDrawerTypes, getTypeByKey } from '../registry';

// Carrega a mensagem do deep link genérico (`?notification-drawer=<type>:<id>`) por id e popula o store via
// `parse` do tipo. Cobre todos os tipos novos sem cada um escrever seu loader; o risco mantém, além disso,
// seu param legado próprio.
const GenericDeepLinkLoader = ({ type, id }: { type: NotificationType; id: string }) => {
  const knock = useKnockClient();
  const openDrawer = useNotificationDrawerStore((s) => s.openDrawer);

  useEffect(() => {
    let cancelled = false;
    knock.messages
      .get(id)
      .then((message) => {
        if (cancelled) return;
        const payload = type.parse(message as unknown as FeedItem);
        if (payload) openDrawer(type.key, payload);
      })
      .catch(() => {
        // Sem a mensagem (id inválido / sem permissão) o drawer fica vazio — graceful.
      });

    return () => {
      cancelled = true;
    };
  }, [knock, id, type, openDrawer]);

  return null;
};

// Resolve o param genérico só quando há tipo válido no registry, provider do Knock montado, e o store ainda
// não tem esse payload. `useKnockClient` fica isolado no loader filho (montado apenas quando o gate passa).
const NotificationGenericDeepLink = () => {
  const { knockInApp } = useFeatureFlags();
  const { getSession } = useUser();
  const userId = getSession()?.user?.id;
  const { knockAPIKey } = Environment.getInstance().getEnvironment();
  const { search } = useLocation();
  const activeType = useNotificationDrawerStore((s) => s.activeType);
  const activePayload = useNotificationDrawerStore((s) => s.activePayload);

  const parsed = parseDrawerParam(new URLSearchParams(search).get(DRAWER_QUERY_PARAM));
  const type = parsed ? getTypeByKey(parsed.type) : undefined;
  const alreadyResolved = !!parsed && activeType === parsed.type && !!activePayload;
  const canResolve = knockInApp && knockAPIKey && userId && !!type && !!parsed && !alreadyResolved;

  if (!(canResolve && type && parsed)) return null;

  return <GenericDeepLinkLoader type={type} id={parsed.id} />;
};

/**
 * Host genérico do drawer de notificação. Despacha pelo registry: monta o `<Drawer>` self-contained de cada
 * tipo com `surfaces.drawer` (cada um se gateia por `activeType === key`) e resolve o deep link genérico.
 * Um único host em AppLayout serve todos os tipos.
 */
const NotificationDrawer = () => (
  <>
    <NotificationGenericDeepLink />
    {getDrawerTypes().map((type) => {
      const TypeDrawer = type.Drawer!;
      return <TypeDrawer key={type.key} />;
    })}
  </>
);

export default NotificationDrawer;
